// AddAssetModal.js - Modal for adding new monitored assets
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAsset } from '../../services/assetService';
import './AddAssetModal.css';

function AddAssetModal({ onClose, onSuccess, limits }) {
    const navigate = useNavigate();
    const [assetType, setAssetType] = useState('domain');
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Check if type is available based on limits
    const canAddDomain = limits?.domains?.remaining > 0;
    const canAddIpRange = limits?.ipRanges?.remaining > 0;

    // Set default to first available type
    React.useEffect(() => {
        if (!canAddDomain && canAddIpRange) {
            setAssetType('ip');
        }
    }, [canAddDomain, canAddIpRange]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!value.trim()) {
            setError('Please enter a value');
            return;
        }

        try {
            setLoading(true);
            await createAsset(assetType, value.trim());
            onSuccess();
        } catch (err) {
            console.error('Failed to create asset:', err);

            // Handle limit errors specially
            if (err.statusCode === 403 && err.upgradeUrl) {
                setError({
                    message: err.message,
                    upgradeMessage: err.upgradeMessage,
                    isLimitError: true
                });
            } else {
                setError({ message: err.message });
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Get placeholder text based on type
    const getPlaceholder = () => {
        switch (assetType) {
            case 'domain': return 'example.com or sub.example.com';
            case 'ip': return '192.168.1.1';
            case 'cidr': return '192.168.1.0/24';
            default: return 'Enter value';
        }
    };

    // Get help text based on type
    const getHelpText = () => {
        switch (assetType) {
            case 'domain':
                return 'Enter a domain name. We\'ll discover subdomains, IPs, and web applications.';
            case 'ip':
                return 'Enter a single IP address for port scanning and vulnerability assessment.';
            case 'cidr':
                return 'Enter an IP range in CIDR notation (max /24 = 256 IPs).';
            default:
                return '';
        }
    };

    return (
        <div className="modal-backdrop" onClick={handleBackdropClick}>
            <div className="add-asset-modal">
                {/* Modal Header */}
                <div className="modal-header">
                    <h2 className="modal-title">
                        <span className="modal-icon">📡</span>
                        Add Monitored Asset
                    </h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit} className="modal-body">
                    {/* Asset Type Selection */}
                    <div className="form-group">
                        <label className="form-label">Asset Type</label>
                        <div className="type-buttons">
                            <button
                                type="button"
                                className={`type-btn ${assetType === 'domain' ? 'active' : ''} ${!canAddDomain ? 'disabled' : ''}`}
                                onClick={() => canAddDomain && setAssetType('domain')}
                                disabled={!canAddDomain}
                            >
                                <span className="type-icon">🌐</span>
                                <span className="type-name">Domain</span>
                                <span className="type-count">
                                    {limits?.domains?.remaining || 0} left
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`type-btn ${assetType === 'ip' ? 'active' : ''} ${!canAddIpRange ? 'disabled' : ''}`}
                                onClick={() => canAddIpRange && setAssetType('ip')}
                                disabled={!canAddIpRange}
                            >
                                <span className="type-icon">📍</span>
                                <span className="type-name">IP Address</span>
                                <span className="type-count">
                                    {limits?.ipRanges?.remaining || 0} left
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`type-btn ${assetType === 'cidr' ? 'active' : ''} ${!canAddIpRange ? 'disabled' : ''}`}
                                onClick={() => canAddIpRange && setAssetType('cidr')}
                                disabled={!canAddIpRange}
                            >
                                <span className="type-icon">🔲</span>
                                <span className="type-name">IP Range</span>
                                <span className="type-count">
                                    {limits?.ipRanges?.remaining || 0} left
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Asset Value Input */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="asset-value">
                            {assetType === 'domain' ? 'Domain Name' : assetType === 'ip' ? 'IP Address' : 'IP Range (CIDR)'}
                        </label>
                        <input
                            id="asset-value"
                            type="text"
                            className="form-input"
                            placeholder={getPlaceholder()}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            autoFocus
                            disabled={loading}
                        />
                        <p className="form-help">{getHelpText()}</p>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className={`form-error ${error.isLimitError ? 'limit-error' : ''}`}>
                            <span className="error-icon">⚠</span>
                            <div className="error-content">
                                <span className="error-message">{error.message}</span>
                                {error.upgradeMessage && (
                                    <p className="upgrade-hint">{error.upgradeMessage}</p>
                                )}
                                {error.isLimitError && (
                                    <button
                                        type="button"
                                        className="btn-upgrade-inline"
                                        onClick={() => navigate('/upgrade')}
                                    >
                                        View Plans
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Info Box */}
                    <div className="info-box">
                        <div className="info-icon">ℹ️</div>
                        <div className="info-content">
                            <strong>What happens next?</strong>
                            <p>
                                After adding, we'll immediately run a baseline scan to discover your attack surface.
                                Then we'll run weekly full scans and daily delta checks to monitor for changes.
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={loading || !value.trim()}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Adding...
                                </>
                            ) : (
                                <>
                                    <span className="btn-icon">+</span>
                                    Add Asset
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddAssetModal;
